import React, { useState, useEffect } from 'react';
import { Shield, Star, Smartphone, Download } from 'lucide-react';
import { statsAPI } from '../services/api';

export const StatsMarquee = () => {
  const [stats, setStats] = useState({ households: 515000, householdsLabel: 'Foyers qui font confiance à Khadam Express', services: 1800000, servicesLabel: 'Services réalisés' });

  useEffect(() => {
    statsAPI.get().then(res => setStats(res.data)).catch(() => {});
  }, []);

  const items = [
    { value: stats.households?.toLocaleString('fr-FR'), label: stats.householdsLabel },
    { value: stats.services?.toLocaleString('fr-FR'), label: stats.servicesLabel },
    { value: stats.households?.toLocaleString('fr-FR'), label: stats.householdsLabel },
    { value: stats.services?.toLocaleString('fr-FR'), label: stats.servicesLabel },
  ];

  return (
    <section className="py-6 bg-gray-50 overflow-hidden">
      <div className="animate-marquee flex whitespace-nowrap">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 mx-12">
            <span className="text-2xl md:text-3xl font-bold text-gray-900">{item.value}</span>
            <span className="text-sm text-gray-500">{item.label}</span>
          </div>
        ))}
        {items.map((item, i) => (
          <div key={`dup-${i}`} className="flex items-center gap-2 mx-12">
            <span className="text-2xl md:text-3xl font-bold text-gray-900">{item.value}</span>
            <span className="text-sm text-gray-500">{item.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

export const TrustBadge = () => {
  const [stats, setStats] = useState({ rating: 4.8, totalReviews: 12450 });

  useEffect(() => {
    statsAPI.get().then(res => setStats(res.data)).catch(() => {});
  }, []);

  return (
  <section className="py-8 bg-white">
    <div className="max-w-7xl mx-auto px-4 flex justify-center">
      <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-5 py-3 shadow-sm">
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              size={18}
              className={i < 4 ? 'text-green-500 fill-green-500' : 'text-green-500 fill-green-200'}
            />
          ))}
        </div>
        <div className="text-sm">
          <span className="font-bold text-gray-900">{stats.rating}</span>
          <span className="text-gray-500"> sur 5 • </span>
          <span className="font-semibold text-gray-700">{(stats.totalReviews || 12450).toLocaleString('fr-FR')}</span>
          <span className="text-gray-500"> avis</span>
        </div>
      </div>
    </div>
  </section>
);
};

export const CoverageSection = () => (
  <section className="py-8 bg-white">
    <div className="max-w-4xl mx-auto px-4">
      <div className="bg-gradient-to-r from-blue-50 to-blue-50/50 rounded-2xl p-6 md:p-8 flex items-center gap-6 border border-blue-100">
        <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <Shield size={28} className="text-blue-500" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-1">La couverture incluse pour tous les services</h3>
          <p className="text-sm text-gray-600">Tous les services réservés via Khadam Express sont couverts. Votre tranquillité est notre priorité.</p>
        </div>
      </div>
    </div>
  </section>
);

export const AppSection = () => (
  <section className="py-16 bg-gray-50">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">
        {/* Text side */}
        <div className="flex-1 text-center md:text-left">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Plus simple avec l'application
          </h2>
          <p className="text-gray-600 mb-8 max-w-md">
            Effectuez, modifiez ou annulez vos réservations partout, en toute simplicité.
          </p>
          <div className="flex gap-3 justify-center md:justify-start">
            <button className="flex items-center gap-2 bg-gray-900 text-white rounded-lg px-4 py-2.5 hover:bg-gray-800 transition-colors">
              <Download size={18} />
              <div className="text-left">
                <div className="text-[10px] opacity-70">Télécharger sur</div>
                <div className="text-sm font-semibold">App Store</div>
              </div>
            </button>
            <button className="flex items-center gap-2 bg-gray-900 text-white rounded-lg px-4 py-2.5 hover:bg-gray-800 transition-colors">
              <Download size={18} />
              <div className="text-left">
                <div className="text-[10px] opacity-70">Disponible sur</div>
                <div className="text-sm font-semibold">Google Play</div>
              </div>
            </button>
          </div>
        </div>

        {/* Phone mockup */}
        <div className="flex-shrink-0">
          <div className="w-[240px] h-[420px] bg-gray-900 rounded-[36px] p-2 shadow-2xl">
            <div className="w-full h-full bg-white rounded-[28px] overflow-hidden flex flex-col">
              <div className="bg-blue-500 px-4 py-3">
                <div className="text-white text-sm font-bold">hraf<span className="text-blue-200">t</span>i</div>
                <div className="text-blue-100 text-[10px] mt-1">Vos services à portée de main</div>
              </div>
              <div className="flex-1 p-3 space-y-2">
                <div className="bg-gray-100 rounded-lg p-2 text-[10px] text-gray-500">Rechercher un service...</div>
                <div className="grid grid-cols-3 gap-2">
                  {['Ménage', 'Bricol.', 'Plomb.', 'Jardin.', 'Démén.', 'Cours'].map((s, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-2 text-center">
                      <div className="w-6 h-6 bg-blue-100 rounded-full mx-auto mb-1"></div>
                      <div className="text-[8px] text-gray-600">{s}</div>
                    </div>
                  ))}
                </div>
                <div className="bg-blue-50 rounded-lg p-2">
                  <div className="text-[9px] font-semibold text-gray-800">Mohammed</div>
                  <div className="text-[8px] text-gray-500">Bricoleur • 180 DH</div>
                  <div className="flex items-center gap-0.5 mt-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);
